import React, { useCallback, useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import usePageTitle from '../../hooks/usePageTitle';
import { dmClient } from '../../services/dmClient';
import {
  getAnalyticsSummary,
  getDenialsByPayer,
  getTurnaround,
  getUnderpayment,
} from '../../services/suiteApi';
import { DASHBOARD_CARDS } from '../../components/workflow/workflowModules';
import { Icon } from '../../components/workflow/WorkflowIcons';
import DenialsCasesTable from '../../components/denials/DenialsCasesTable';
import { downloadCaseDocuments } from '../../components/denials/downloadCaseDocuments';

const QUICK_ACTIONS = [
  { label: 'New intake',          to: '/intake/new',     hint: 'Upload a denial pack' },
  { label: 'Scrub a claim',       to: '/ClaimRight',     hint: 'Run 837 + code checks' },
  { label: 'Check eligibility',   to: '/AuthEligibility',hint: '270/271 or PA submit' },
];

const QUEUE_TABS = [
  { id: 'mine',   label: 'Personal Queue' },
  { id: 'shared', label: 'Shared Queue'   },
];

function fmtMoney(n) {
  if (n == null || Number.isNaN(Number(n))) return '$0';
  const v = Number(n);
  if (Math.abs(v) >= 1_000_000) return `$${(v / 1_000_000).toFixed(1)}M`;
  if (Math.abs(v) >= 1_000) return `$${(v / 1_000).toFixed(1)}k`;
  return `$${v.toFixed(0)}`;
}

function Tile({ label, value, sub }) {
  return (
    <div className="dm-tile">
      <div className="dm-tile__label">{label}</div>
      <div className="dm-tile__value">{value}</div>
      {sub && <div className="dm-tile__sub">{sub}</div>}
    </div>
  );
}

export default function DashboardPage() {
  usePageTitle('Dashboard');
  const navigate = useNavigate();

  const [summary, setSummary]       = useState(null);
  const [byPayer, setByPayer]       = useState([]);
  const [turn, setTurn]             = useState(null);
  const [under, setUnder]           = useState(null);
  const [error, setError]           = useState(null);

  const [queue, setQueue]           = useState('mine');
  const [queueItems, setQueueItems] = useState([]);
  const [queueLoading, setQueueLoading] = useState(false);
  const [queueError, setQueueError] = useState(null);
  const [downloadingId, setDownloadingId] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const [s, p, t, u] = await Promise.allSettled([
          getAnalyticsSummary(),
          getDenialsByPayer(),
          getTurnaround(),
          getUnderpayment(),
        ]);
        if (cancelled) return;
        if (s.status === 'fulfilled') setSummary(s.value?.data || null);
        if (p.status === 'fulfilled') {
          const v = p.value?.data;
          setByPayer(Array.isArray(v) ? v : (v?.items || []));
        }
        if (t.status === 'fulfilled') setTurn(t.value?.data || null);
        if (u.status === 'fulfilled') setUnder(u.value?.data || null);
      } catch (e) {
        if (!cancelled) setError(e?.message || 'Failed to load dashboard');
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const loadQueue = useCallback(async (q) => {
    setQueueLoading(true);
    setQueueError(null);
    try {
      const resp = await dmClient.get('/denials', {
        params: { queue: q, pageSize: 25 },
      });
      const data = resp.data?.data || {};
      setQueueItems(data.items || data.cases || []);
    } catch (e) {
      const msg = e?.response?.data?.message
        || e?.message
        || 'Failed to load queue';
      setQueueError(msg);
      setQueueItems([]);
    } finally {
      setQueueLoading(false);
    }
  }, []);

  useEffect(() => { loadQueue(queue); }, [queue, loadQueue]);

  const handleOpen = useCallback((caseId, { status: rowStatus, executionId } = {}) => {
    const wantsRun = !executionId
      && String(rowStatus || '').toLowerCase() === 'new';
    navigate(`/denials/${caseId}`, { state: { autostart: wantsRun } });
  }, [navigate]);

  const handleDownload = useCallback(async (caseId, { externalId } = {}) => {
    setDownloadingId(caseId);
    try {
      await downloadCaseDocuments(caseId, { externalId });
    } catch (e) {
      const msg = e?.response?.data?.message
        || (e?.response?.status === 404 ? 'No uploaded documents on this case' : null)
        || e?.message
        || 'Download failed';
      setQueueError(msg);
    } finally {
      setDownloadingId(null);
    }
  }, []);

  const claims = summary?.claims || {};
  const denials = summary?.denials || {};
  const openDenials = denials.OpenCases ?? denials.openCases
    ?? (typeof denials.TotalDenials === 'number'
        ? denials.TotalDenials - (denials.ClosedCases || 0)
        : 0);
  const recovery = under?.totalPotentialRecovery ?? under?.totalUnderpayment ?? 0;
  const tatHours = turn?.medianTurnaroundHours ?? turn?.median ?? null;
  const appealsFiled = denials.AppealsFiled7d ?? denials.appealsFiled7d ?? 0;
  const claimsInFlight = claims.SubmittedClaims ?? claims.OpenClaims
    ?? claims.TotalClaims ?? 0;

  return (
    <div className="dm-page dm-dashboard">
      <header className="dm-page__header">
        <h1 className="dm-page__title">Pick a workflow</h1>
        <p className="dm-page__lede">
          Start in the use case you need. Each workflow follows the same path:
          choose an action, upload your files, run it, then review, download
          and chat about the result. New here? Take the <Link to="/test-drive">Test Drive</Link>{' '}
          to run a sample case in seconds.
        </p>
      </header>

      <section className="dm-workflow-cards" aria-label="Use cases">
        {DASHBOARD_CARDS.map((c) => {
          const IconComp = c.icon;
          return (
            <Link
              key={c.id}
              to={c.to}
              className={`dm-workflow-card${c.highlight ? ' dm-workflow-card--accent' : ''}`}
            >
              <span className="dm-workflow-card__ic" aria-hidden="true">
                <IconComp />
              </span>
              <span className="dm-workflow-card__name">{c.name}</span>
              <span className="dm-workflow-card__desc">{c.short}</span>
              <span className="dm-workflow-card__cta" aria-hidden="true">
                Open <Icon.arrow />
              </span>
            </Link>
          );
        })}
      </section>

      {error && <div className="dm-banner dm-banner--error">{error}</div>}

      <section className="dm-tile-grid">
        <Tile label="Open denials"      value={openDenials} />
        <Tile label="Recovery potential" value={fmtMoney(recovery)} />
        <Tile label="Appeals filed (7d)" value={appealsFiled} />
        <Tile label="Claims in flight"  value={claimsInFlight} />
        <Tile label="Median turnaround" value={tatHours != null ? `${tatHours}h` : '--'} />
        <Tile label="Top payer (denied)"
                value={byPayer?.[0]?.payerName || byPayer?.[0]?.PayerName || '--'}
                sub={byPayer?.[0]?.totalDenied != null ? fmtMoney(byPayer[0].totalDenied) : ''} />
      </section>

      <section className="dm-card">
        <div className="dm-card__title">
          <span>Case queue</span>
          <Link to="/denials" className="dm-card__more">All denials</Link>
        </div>

        <div className="denials-queue-tabs" role="tablist" aria-label="Case queue">
          {QUEUE_TABS.map((q) => (
            <button
              type="button"
              key={q.id}
              role="tab"
              aria-selected={queue === q.id}
              className={`denials-queue-tab${queue === q.id ? ' is-active' : ''}`}
              onClick={() => setQueue(q.id)}
            >
              {q.label}
            </button>
          ))}
        </div>

        {queueError && (
          <div className="denials-alert denials-alert--error">{queueError}</div>
        )}

        <DenialsCasesTable
          items={queueItems}
          loading={queueLoading}
          emptyMessage={
            queue === 'mine'
              ? 'No cases in your Personal Queue yet. Upload a denial pack from Intake to populate it.'
              : 'No cases in the Shared Queue for this account.'
          }
          onOpen={handleOpen}
          onDownload={handleDownload}
          downloadingId={downloadingId}
        />
      </section>

      <section className="dm-card">
        <div className="dm-card__title">Quick actions</div>
        <div className="dm-quick-actions">
          {QUICK_ACTIONS.map((qa) => (
            <Link to={qa.to} key={qa.to} className="dm-quick-action">
              <span className="dm-quick-action__label">{qa.label}</span>
              <span className="dm-quick-action__hint">{qa.hint}</span>
            </Link>
          ))}
        </div>
      </section>
    </div>
  );
}
